import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  Bell01,
  BellRinging01,
  BookClosed,
  BookOpen01,
  Calendar,
  CalendarCheck01,
  CheckSquareBroken,
  CurrencyDollarCircle,
  Eye,
  File02,
  File06,
  FolderCode,
  GraduationHat02,
  Menu01,
  PieChart03,
  Star01,
  Stars03,
  Target04,
  TrendUp01,
} from "@untitledui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import MastercardLogo from "@/assets/images/MastercardLogo.svg";
import LanguageSwitcher from "@/Components/LanguageSwitcher";
import TimezoneBar from "@/Components/TimezoneBar";
import TourGuide from "@/Components/TourGuide";
import { useAuth } from "@/hooks/useAuth";
import TooltipButton from "@/shared/components/TooltipButton";
import { useAppSelector } from "@/store/hooks";
import {
  fetchUserDetailsByEmail,
  resetRoleState,
  selectFeatures,
  selectFeaturesLoading,
  selectFeaturesStatus,
  selectLanguageSwitchIcon,
  selectNotificationIcon,
  selectTourIcon,
} from "@/store/roleSlice";
import { initTheme } from "@/store/theme";

import NotificationsDropdown from "../Notifications/component/NotificationDropDown";
import ProfileMenu from "./components/ProfileMenu";
import type { NavbarProps } from "./types/navbar";

const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

const Navbar: React.FC<NavbarProps> = ({ drawerOpen, setDrawerOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = useAppSelector(selectFeatures);
  const featuresLoading = useAppSelector(selectFeaturesLoading);
  const featureStatus = useAppSelector(selectFeaturesStatus);
  const showTourIcon = useAppSelector(selectTourIcon);
const showNotificationIcon = useAppSelector(selectNotificationIcon);
const showLanguageSwitch = useAppSelector(selectLanguageSwitchIcon);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch<any>(initTheme());
  }, [dispatch]);

  const { accounts } = useMsal();
  const { account, login, logout } = useAuth();
  const isAuthenticated = useIsAuthenticated();

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const [tourTrigger, setTourTrigger] = useState(0);

  const getColor = (key: string) => {
    const colors = [
      "bg-red-100 text-red-600",
      "bg-blue-100 text-blue-600",
      "bg-green-100 text-green-600",
      "bg-yellow-100 text-yellow-600",
      "bg-purple-100 text-purple-600",
      "bg-pink-100 text-pink-600",
      "bg-indigo-100 text-indigo-600",
    ];

    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");

  const userPrincipalName = accounts[0]?.username || account?.userPrincipalName || "";

  const widgetIconMap: Record<string, React.ReactNode> = {
    "dashboard.quiklinks": <Stars03 className="h-4 w-4" />,
    "dashboard.myactiveitems": <TrendUp01 className="h-4 w-4" />,
    "dashboard.taskpriorities": <CheckSquareBroken className="h-4 w-4" />,
    "dashboard.notifications": <BellRinging01 className="h-4 w-4" />,
    "dashboard.keyguidelines": <BookOpen01 className="h-4 w-4" />,
    "dashboard.upcomingmeetings": <CalendarCheck01 className="h-4 w-4" />,
    "dashboard.impacttargets": <Target04 className="h-4 w-4" />,
    "dashboard.teamcalender": <Calendar className="h-4 w-4" />,
    "dashboard.partnerdirectory": <BookClosed className="h-4 w-4" />,
    "dashboard.learninginsights": <GraduationHat02 className="h-4 w-4" />,
    "dashboard.analyticsinsights": <PieChart03 className="h-4 w-4" />,
    "dashboard.programresources": <FolderCode className="h-4 w-4" />,
    "dashboard.oversight": <Eye className="h-4 w-4" />,
    "dashboard.submissions": <File06 className="h-4 w-4" />,
    "dashboard.partnersubmissions": <File02 className="h-4 w-4" />,
    "dashboard.disbursementrequest": <CurrencyDollarCircle className="h-4 w-4" />,
  };
  const widgetSidebarConfig: Record<string, { label: string }> = {
    "dashboard.quiklinks": { label: t("widgetSidebarConfig.QuickAccessApps") },
    "dashboard.myactiveitems": { label: t("widgetSidebarConfig.ActivePrograms") },
    "dashboard.taskpriorities": { label: t("widgetSidebarConfig.TaskPriorities") },
    "dashboard.notifications": { label: t("widgetSidebarConfig.Notifications") },
    "dashboard.keyguidelines": { label: t("widgetSidebarConfig.KeyGuidelines") },
    "dashboard.upcomingmeetings": { label: t("widgetSidebarConfig.UpcomingMeetings") },
    "dashboard.impacttargets": { label: t("widgetSidebarConfig.FinanceTargets") },
    "dashboard.teamcalender": { label: t("widgetSidebarConfig.TeamCalendar") },
    "dashboard.partnerdirectory": { label: t("widgetSidebarConfig.PartnersDirectory") },
    "dashboard.learninginsights": { label: t("widgetSidebarConfig.LearningInsights") },
    "dashboard.analyticsinsights": { label: t("widgetSidebarConfig.FinancialAnalytics") },
    "dashboard.programresources": { label: t("widgetSidebarConfig.ProgramResources") },
    "dashboard.oversight": { label: t("widgetSidebarConfig.Oversight") },
    "dashboard.submissions": { label: t("widgetSidebarConfig.MySubmissions") },
    "dashboard.partnersubmissions": { label: t("widgetSidebarConfig.PartnerSubmissions") },
    "dashboard.disbursementrequest": { label: t("widgetSidebarConfig.DisbursementRequests") },
  };

  const widgetSidebarItems = features
    .filter((f) => f.access === true || f.access === 1)
    .map((f) => ({
      key: f.feature_code,
      ...widgetSidebarConfig[f.feature_code],
    }))
    .filter((item) => item.label);

  const scrollToWidget = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const navbarHeight = 72; // adjust if needed
    const y = el.getBoundingClientRect().top + window.scrollY - navbarHeight;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  return (
    <>
      {featureStatus === "failed" && (
        <div className="fixed inset-0 flex items-center justify-center z-[999] bg-[var(--bg-surface)]">
          <div className="flex flex-col items-center gap-3 text-red-500">
            <span className="text-lg font-medium">Failed to Load Widgets</span>
            <button
              onClick={() => {
                dispatch(resetRoleState());
                if (userPrincipalName) {
                  dispatch(fetchUserDetailsByEmail(userPrincipalName));
                }
              }}
              className="px-4 py-2 text-sm bg-red-100 text-red-600 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-[1000]">
        <div className="w-full border-b bg-[var(--nav-bg)] border-[var(--nav-border)]">
          <div className="mx-auto px-3 sm:px-4 lg:px-6">
            <div className="grid h-14 grid-cols-[auto_1fr_auto] items-center gap-3 text-[var(--text-primary)]">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--sidebar-hover)]"
                  onClick={() => setDrawerOpen((v) => !v)}
                >
                  <Menu01 className="h-4 w-4" />
                </button>

                <img
                  src={MastercardLogo}
                  alt="Mastercard Foundation"
                  className="h-8 w-auto cursor-pointer"
                  onClick={() => navigate("/", { replace: true })}
                />
              </div>

              <div className="relative mx-auto hidden min-w-0 items-center sm:flex">
                <TimezoneBar upn={userPrincipalName} />
              </div>

              <div className="flex flex-none items-center gap-1">

                {showTourIcon && (
                  <>
                    <TooltipButton
                      position="bottom"
                      onClick={() => setTourTrigger((v) => v + 1)}
                      tooltip={t("components.navBar.tour", { defaultValue: "Take a Tour" })}
                      icon={<span className="pointer-events-none">
                      <Star01 className="h-4 w-4" />
                    </span>}
                    />
                    <TourGuide userPrincipalName={userPrincipalName} flag={tourTrigger} />
                  </>
                )}

                {showNotificationIcon && (
                  <div className="notification-icon relative" ref={notifRef}>
                    <TooltipButton
                      tooltip={t("components.navBar.notifications", {
                        defaultValue: "Notifications",
                      })}
                      onClick={() => setNotifOpen((v) => !v)}
                      position="bottom"
                      icon={<Bell01 className="h-4 w-4" />}
                    />

                    {notifOpen && (
                      <div
                        className="absolute right-0 mt-2 w-[360px] rounded-lg shadow-xl border z-[1000]"
                        style={{
                          background: "var(--bg-surface)",
                          borderColor: "var(--border-color)",
                        }}
                      >
                        <NotificationsDropdown />
                      </div>
                    )}
                  </div>
                )}

                {showLanguageSwitch && (
                  <div className="language-switcher relative">
                    <TooltipButton
                      tooltip={t("components.navBar.language", { defaultValue: "Languages" })}
                      position="bottom"
                      icon={<LanguageSwitcher upn={userPrincipalName} />}
                    />
                  </div>
                )}


                <div className="profile-menu flex items-center gap-2">
                  <ProfileMenu
                    displayName={displayName}
                    email={userPrincipalName}
                    isAuthenticated={isAuthenticated}
                    onLogin={login}
                    onLogout={() => {
                      logout();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <aside
        className={cn(
          "fixed left-0 top-14 bottom-0 overflow-y-auto border-r z-[900] transition-all duration-500",
          "bg-[var(--sidebar-bg)] border-[var(--border-color)]",
          drawerOpen ? "w-[300px]" : "w-[64px]"
        )}
      >
        <nav className="mt-2 px-2 pb-24">
          <ul className="space-y-1">
            {!featuresLoading &&
              featureStatus === "succeeded" &&
              widgetSidebarItems.map((item) => {
                return (
                  <li key={item.key}>
                    <button
                      onClick={() => scrollToWidget(item.key)}
                      className={cn(
                        "flex items-center rounded-md text-sm font-medium transition-all w-full",
                        drawerOpen
                          ? "gap-3 px-3 py-2"
                          : "w-full h-10 flex items-center justify-center",
                        "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-hover)]"
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center justify-center w-6 h-6 shrink-0 rounded-full text-xs font-semibold",
                          !widgetIconMap[item.key] && getColor(item.key)
                        )}
                      >
                        {widgetIconMap[item.key]
                          ? widgetIconMap[item.key]
                          : item.label?.charAt(0).toUpperCase()}
                      </div>

                      {drawerOpen && <span className="truncate">{item.label}</span>}
                    </button>
                  </li>
                );
              })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Navbar;
